"use client";

import React, { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Activity, Clock, Users, ShieldCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedNumber({
  value,
  isVisible
}: {
  value: number;
  isVisible: boolean;
}) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2000;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCurrent(Math.floor(easeProgress * value));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCurrent(value);
      }
    };
    window.requestAnimationFrame(step);
  }, [value, isVisible]);
  return <>{current}</>;
}
const statsData = [{
  icon: Clock,
  value: 99,
  suffix: ".9%",
  title: "99.9% Uptime Commitment",
  description: "Dedicated high-speed Ethernet infrastructure.",
  progressValue: 99
}, {
  icon: Users,
  value: 3,
  suffix: "+",
  title: "3+ Years Specialized Experience",
  description: "Proven track record in medical and legal settings.",
  progressValue: 95
}, {
  icon: ShieldCheck,
  value: 100,
  suffix: "%",
  title: "HIPAA & Legal Certified",
  description: "Full compliance with international privacy standards.",
  progressValue: 100
}, {
  icon: Activity,
  value: 0,
  suffix: "",
  title: "Zero Data Breaches",
  description: "Secure, encrypted communication environments.",
  progressValue: 100
}];
export default function ClinicalImpactByTheNumbers() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223267" id="operational-excellence" className="bg-background text-foreground py-20 lg:py-24 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none opacity-40" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none opacity-20" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        
        {}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tighter uppercase">
            Technical <span className="text-primary">Precision in Practice</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            GLYPHIC S.A.S. maintains the highest technical standards in the language service industry, 
            ensuring every connection is secure, stable, and precise.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsData.map((stat, i) => <div key={i} data-index={i} className={`relative group rounded-sm border border-border bg-card p-6 shadow-sm transition-all duration-700 ease-out hover:bg-card hover:border-primary hover:shadow-md hover:-translate-y-1 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {}
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-sm bg-primary/10 text-primary ring-1 ring-primary/20">
                <stat.icon className="h-6 w-6" />
              </div>

              {}
              <div className="mb-1 flex items-baseline gap-1">
                <span className="text-5xl md:text-6xl font-mono font-bold text-primary tracking-tighter">
                  <AnimatedNumber value={stat.value} isVisible={sectionVisible} />
                </span>
                <span className="text-3xl md:text-4xl font-mono font-bold text-primary/80 tracking-tighter">
                  {stat.suffix}
                </span>
              </div>

              {}
              <h3 className="text-xl font-default font-bold text-card-foreground mb-2 uppercase tracking-tight">
                {stat.title}
              </h3>
              <p className="text-[14px] text-muted-foreground font-default font-normal mb-6 leading-relaxed">
                {stat.description}
              </p>

              {}
              <div className="absolute bottom-6 left-6 right-6">
                <Progress value={sectionVisible ? stat.progressValue : 0} className="h-1 bg-muted rounded-none" />
              </div>
            </div>)}
        </div>

      </div>
    </section>;
}